/**
 * Futures Control - Spreads form dialog (New / Edit / Copy).
 * 1:1 với Access `frmFuturesQrySpreadsAdd`.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import DateTextField from '../../components/DateTextField'
import NumericInput from './NumericInput'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  useCreateFuturesSpreadMutation,
  useGetFuturesOrderFlagsQuery,
  useGetFuturesOrderPartnersQuery,
  useGetFuturesOrderTradeTypesQuery,
  useGetSpreadSymbolsQuery,
  useLazyCheckSpreadOrderNoExistsQuery,
  useLazySuggestSpreadOrderNoQuery,
  useUpdateFuturesSpreadMutation,
} from '../../store/api/futuresControlApi'
import { futuresApiErrorMessage } from './futuresApiErrorMessage'
import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresFormDialogTitleSx,
} from './futuresAccessUi'

export type SpreadFormRow = Record<string, unknown> & { id: string }
export type SpreadFormMode = 'new' | 'edit' | 'copy'

export interface SpreadsFormDialogProps {
  open: boolean
  mode: SpreadFormMode
  sourceRow?: SpreadFormRow | null
  onClose: () => void
  canEdit: boolean
}

const BUY_SELL_OPTIONS = ['BUY', 'SELL'] as const

// Skill `/convert-form` readonly standard: neutral gray thay vì `action.hover`
// (overlay phớt hồng trên theme TLC cream).
const readonlySx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': {
    color: 'text.secondary',
    fontWeight: 600,
    cursor: 'default',
  },
} as const

function str(v: unknown): string {
  if (v == null || v === '') return ''
  return String(v)
}

function numOrUndefined(v: string): number | undefined {
  if (v.trim() === '') return undefined
  const n = Number(v.replace(/,/g, ''))
  return Number.isFinite(n) ? n : undefined
}

/** Access: `full_symbol = IIf([symbol]<>"" And [roll_step]<>"", Left([symbol],3) & "S" & [roll_step] & Right([symbol],3), "")` */
function buildFullSymbol(symbol: string, rollStep: string): string {
  const s = symbol.trim()
  const r = rollStep.trim()
  if (!s || !r) return ''
  if (s.length < 6) return ''
  return `${s.slice(0, 3)}S${r}${s.slice(-3)}`
}

export default function SpreadsFormDialog({ open, mode, sourceRow, onClose, canEdit }: SpreadsFormDialogProps) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'
  const isCopy = mode === 'copy'
  const isNew = mode === 'new'

  const [orderNumber, setOrderNumber] = useState('')
  const [date, setDate] = useState('')
  const [buyOrSell, setBuyOrSell] = useState('')
  const [symbol, setSymbol] = useState('')
  const [rollStep, setRollStep] = useState('')
  const [filledPrice, setFilledPrice] = useState('')
  const [typeOfTrade, setTypeOfTrade] = useState('')
  const [lot, setLot] = useState('')
  const [partner, setPartner] = useState('')
  const [fromPrice, setFromPrice] = useState('')
  const [flag, setFlag] = useState('Hedge')

  const [createRow, createState] = useCreateFuturesSpreadMutation()
  const [updateRow, updateState] = useUpdateFuturesSpreadMutation()
  const [touched, setTouched] = useState(false)
  const busy = createState.isLoading || updateState.isLoading

  const [fetchSuggested] = useLazySuggestSpreadOrderNoQuery()
  const [triggerCheck] = useLazyCheckSpreadOrderNoExistsQuery()

  /* Pre-save check (skill access-suggest) */
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  useEffect(() => {
    if (!open) return
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
  }, [open, mode])

  const handleOrderNumberBlur = useCallback(async () => {
    if (mode === 'edit') return
    const val = orderNumber.trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheck({ orderNo: val }).unwrap()
      if (!check.exists) return
      const next = await fetchSuggested(undefined, false).unwrap()
      const newVal = (next.orderNumber || '').trim()
      if (newVal && newVal !== val) {
        setOrderNumber(newVal)
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      // BE sẽ raise lúc Save nếu thật sự trùng.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }, [mode, orderNumber, triggerCheck, fetchSuggested])

  // Comboboxes (Access RowSources).
  const symbolsQ = useGetSpreadSymbolsQuery(undefined, { skip: !open })
  const tradeTypesQ = useGetFuturesOrderTradeTypesQuery(undefined, { skip: !open })
  const partnersQ = useGetFuturesOrderPartnersQuery(undefined, { skip: !open })
  const flagsQ = useGetFuturesOrderFlagsQuery(undefined, { skip: !open })

  const symbolOpts = symbolsQ.data ?? []
  const tradeTypeOpts = tradeTypesQ.data ?? []
  const partnerOpts = partnersQ.data ?? []
  const flagOpts = (flagsQ.data ?? []).map((f) => f.flag)

  const reset = useCallback(() => {
    setOrderNumber('')
    setDate('')
    setBuyOrSell('')
    setSymbol('')
    setRollStep('')
    setFilledPrice('')
    setTypeOfTrade('')
    setLot('')
    setPartner('')
    setFromPrice('')
    setFlag('Hedge')
    setTouched(false)
  }, [])

  // Init khi mở dialog — preload theo từng mode.
  useEffect(() => {
    if (!open) {
      reset()
      return
    }
    const r = sourceRow
    if (isNew) {
      // Access `btnNew_Click`: order_number = Suggest_IndexName, date = Now(), flag mặc định "Hedge".
      const today = new Date().toISOString().slice(0, 10)
      setOrderNumber('')
      setDate(today)
      setBuyOrSell('')
      setSymbol('')
      setRollStep('')
      setFilledPrice('')
      setTypeOfTrade('')
      setLot('')
      setPartner('')
      setFromPrice('')
      setFlag('Hedge')
      void fetchSuggested()
        .unwrap()
        .then((res) => setOrderNumber(res.orderNumber))
        .catch(() => undefined)
      return
    }
    if (!r) return
    // Edit / Copy — load đầy đủ trường từ row đang chọn (datasheet bám MV).
    const sourceOrderNo = str(r['orderNumber'])
    if (isCopy) {
      const today = new Date().toISOString().slice(0, 10)
      setOrderNumber('')
      setDate(today)
      void fetchSuggested()
        .unwrap()
        .then((res) => setOrderNumber(res.orderNumber))
        .catch(() => undefined)
    } else {
      setOrderNumber(sourceOrderNo)
      setDate(str(r['date']).slice(0, 10))
    }
    setBuyOrSell(str(r['buyOrSell']))
    // Access btnEdit: symbol field nhận từ `from_month1` của leg 1, không phải symbol đã build sẵn.
    setSymbol(str(r['fromMonth1'] ?? r['symbol']))
    setRollStep(str(r['rollStep'] ?? ''))
    setFilledPrice(str(r['filledPrice'] ?? ''))
    setTypeOfTrade(str(r['typeOfTrade']))
    setLot(str(r['lot'] ?? ''))
    setPartner(str(r['partner']))
    setFromPrice(str(r['fromPrice'] ?? ''))
    // Flag → Access dùng DLookup ở leg `-L1`; trên web em copy `flag` nếu MV trả về, fallback "Hedge".
    setFlag(str(r['flag']) || 'Hedge')
  }, [open, isNew, isCopy, sourceRow, reset, fetchSuggested])

  const fullSymbol = useMemo(() => buildFullSymbol(symbol, rollStep), [symbol, rollStep])

  // Per-field error flags — mirrors LocalPFormDialog `error={!!errors.field}` pattern.
  // Combobox fields flag both missing and not-in-list values so user can't sneak
  // a typed-but-invalid value past Save.
  const fieldErrors = useMemo(() => {
    const on = orderNumber.trim()
    const bs = buyOrSell.trim()
    const sym = symbol.trim()
    const tt = typeOfTrade.trim()
    const pt = partner.trim()
    const fl = flag.trim()
    return {
      orderNumber: !on || !on.toUpperCase().startsWith('SP-'),
      date: !date.trim(),
      buyOrSell: !bs || !(BUY_SELL_OPTIONS as readonly string[]).includes(bs),
      symbol: !sym || !symbolOpts.includes(sym),
      typeOfTrade: !tt || !tradeTypeOpts.includes(tt),
      partner: !pt || !partnerOpts.includes(pt),
      flag: !fl || !flagOpts.includes(fl),
    }
  }, [orderNumber, date, buyOrSell, symbol, typeOfTrade, partner, flag, symbolOpts, tradeTypeOpts, partnerOpts, flagOpts])

  /** Access `btnSave_Click` errCheck 1..4. */
  const validationError = useMemo((): string | null => {
    if (!open) return null
    const on = orderNumber.trim()
    if (!on) return 'Missing Order Number'
    if (!on.startsWith('SP-')) return "Please input Order Number with format 'SP-*'!"
    // errCheck 2 (duplicate) handled server-side — surface qua futuresApiErrorMessage.
    if (!date.trim()) return 'Please choose Date!'
    if (fieldErrors.buyOrSell) return 'Please choose Buy/Sell from list!'
    if (fieldErrors.symbol) return 'Please choose Symbol from list!'
    if (fieldErrors.typeOfTrade) return 'Please choose Type of trade from list!'
    if (fieldErrors.partner) return 'Please choose Partner from list!'
    if (fieldErrors.flag) return 'Please choose Flag from list!'
    return null
  }, [open, orderNumber, date, fieldErrors])

  const handleSave = async () => {
    if (precheckBusyRef.current) return
    setTouched(true)
    if (validationError) {
      snack.error(validationError)
      return
    }
    const body = {
      date: date || undefined,
      buyOrSell: buyOrSell || undefined,
      // Access: cột lưu xuống `symbol` là `full_symbol` đã build.
      symbol: fullSymbol || symbol || undefined,
      rollStep: numOrUndefined(rollStep),
      filledPrice: numOrUndefined(filledPrice),
      typeOfTrade: typeOfTrade || undefined,
      lot: numOrUndefined(lot),
      partner: partner || undefined,
      fromPrice: numOrUndefined(fromPrice),
      flag: flag || undefined,
    }
    try {
      if (isEdit) {
        await updateRow({ orderNumber: orderNumber.trim(), body }).unwrap()
        snack.success('Successfully Updated')
      } else {
        await createRow({ orderNumber: orderNumber.trim(), ...body }).unwrap()
        snack.success('Successfully Added')
      }
      onClose()
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  const dialogTitle = useMemo(() => {
    if (isEdit) return 'Futures Control - Spreads (Edit)'
    if (isCopy) return 'Futures Control - Spreads (Copy)'
    return 'Futures Control - Spreads (New)'
  }, [isEdit, isCopy])

  // Access: order_number disabled khi Edit; date disabled cũng nhưng VBA chỉ disable order_number;
  // Date có thể chỉnh ở Edit (Access lưu lại). Em theo Access.
  const orderNumberDisabled = isEdit

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(720px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle sx={futuresFormDialogTitleSx}>
        <Typography component="span" sx={{ flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}>
          {dialogTitle}
        </Typography>
        <Box sx={futuresFormDialogActionsBoxSx}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={busy || !canEdit}
            sx={futuresFormDialogActionBtnSx}
          >
            {busy ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} sx={futuresFormDialogActionBtnSx}>
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          pt: 0.75,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
          '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
          '& .MuiInputLabel-shrink': { fontWeight: 600 },
        }}
      >
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>
            Order No <b>{regenNotice.from}</b> was just created — suggested
            new code <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        <Box sx={{ maxWidth: 820, mx: 'auto', pb: 1.5 }}>
          <Grid container spacing={0.75}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Order No *"
                size="small"
                fullWidth
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                onBlur={() => void handleOrderNumberBlur()}
                disabled={orderNumberDisabled || !canEdit}
                error={touched && fieldErrors.orderNumber}
                helperText={
                  precheckBusy
                    ? 'Verifying order number…'
                    : touched && fieldErrors.orderNumber
                      ? "Please input Order Number with format 'SP-*'"
                      : 'SP-… (auto suggest when New)'
                }
                sx={orderNumberDisabled ? readonlySx : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTextField
                label="Date *"
                size="small"
                fullWidth
                value={date}
                onChange={(v) => setDate(v)}
                disabled={!canEdit}
                autoFocus={isNew && canEdit}
                error={touched && fieldErrors.date}
                helperText={touched && fieldErrors.date ? 'Please choose Date' : undefined}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                autoHighlight
                options={BUY_SELL_OPTIONS as unknown as string[]}
                value={buyOrSell || null}
                disabled={!canEdit}
                isOptionEqualToValue={(opt, v) => opt === v}
                onChange={(_, v) => setBuyOrSell(typeof v === 'string' ? v : (v ?? ''))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buy / Sell *"
                    size="small"
                    fullWidth
                    error={touched && fieldErrors.buyOrSell}
                    helperText={touched && fieldErrors.buyOrSell ? 'Please choose Buy/Sell from list' : undefined}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                autoHighlight
                options={tradeTypeOpts}
                loading={tradeTypesQ.isLoading || tradeTypesQ.isFetching}
                value={typeOfTrade || null}
                disabled={!canEdit}
                isOptionEqualToValue={(opt, v) => opt === v}
                onChange={(_, v) => setTypeOfTrade(typeof v === 'string' ? v : (v ?? ''))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Type of trade *"
                    size="small"
                    fullWidth
                    error={touched && fieldErrors.typeOfTrade}
                    helperText={touched && fieldErrors.typeOfTrade ? 'Please choose Type of trade from list' : undefined}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                autoHighlight
                options={symbolOpts}
                loading={symbolsQ.isLoading || symbolsQ.isFetching}
                value={symbol || null}
                disabled={!canEdit}
                isOptionEqualToValue={(opt, v) => opt === v}
                onChange={(_, v) => setSymbol(typeof v === 'string' ? v : (v ?? ''))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Symbol *"
                    size="small"
                    fullWidth
                    error={touched && fieldErrors.symbol}
                    helperText={touched && fieldErrors.symbol ? 'Please choose Symbol from list' : undefined}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumericInput
                label="Roll Step"
                size="small"
                fullWidth
                integer
                value={rollStep}
                onChange={setRollStep}
                disabled={!canEdit}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Full Symbol"
                size="small"
                fullWidth
                value={fullSymbol}
                disabled
                sx={readonlySx}
                helperText="Auto build from Symbol + Roll Step (Left3 + S + step + Right3)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <NumericInput
                label="Filled Price"
                size="small"
                fullWidth
                value={filledPrice}
                onChange={setFilledPrice}
                disabled={!canEdit}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumericInput
                label="From Price"
                size="small"
                fullWidth
                value={fromPrice}
                onChange={setFromPrice}
                disabled={!canEdit}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <NumericInput
                label="Lot"
                size="small"
                fullWidth
                integer
                value={lot}
                onChange={setLot}
                disabled={!canEdit}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                autoHighlight
                options={partnerOpts}
                loading={partnersQ.isLoading || partnersQ.isFetching}
                value={partner || null}
                disabled={!canEdit}
                isOptionEqualToValue={(opt, v) => opt === v}
                onChange={(_, v) => setPartner(typeof v === 'string' ? v : (v ?? ''))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Partner *"
                    size="small"
                    fullWidth
                    error={touched && fieldErrors.partner}
                    helperText={touched && fieldErrors.partner ? 'Please choose Partner from list' : undefined}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                size="small"
                autoHighlight
                options={flagOpts}
                loading={flagsQ.isLoading || flagsQ.isFetching}
                value={flag || null}
                disabled={!canEdit}
                isOptionEqualToValue={(opt, v) => opt === v}
                onChange={(_, v) => setFlag(typeof v === 'string' ? v : (v ?? ''))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Flag *"
                    size="small"
                    fullWidth
                    error={touched && fieldErrors.flag}
                    helperText={touched && fieldErrors.flag ? 'Please choose Flag from list' : undefined}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
